import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BaseHttpService } from '@shared-services/base-http-service';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root' // Single instance provided in root
})
export class ErrorService {

  private errorUrl = '/WebLogger/';

  protected http = inject(HttpClient);

  readonly friendlyName: string = "ErrorService";

  constructor() { }

  logError(msg: string, logger: string, error: any, logAsWarning: boolean = false): void {
    let userAgent = null;
    let platform = null;
    let location = null;
    let errorString = null;

    try {
      try {
        // Try to get browser details
        if (typeof (navigator) != 'undefined') {
          if (typeof (navigator.userAgent) != 'undefined') { userAgent = navigator.userAgent; }
          if (typeof (navigator.platform) != 'undefined') { platform = navigator.platform; }
        }

        // Try to get location details
        if (typeof (window) != 'undefined' && typeof (window.location) != 'undefined' && typeof (window.location.href) != 'undefined') {
          location = window.location.href;
        }

        let jsonFriendlyErrorReplacer = function (key: any, value: any) {
          if (value instanceof Error) {
            return Object.assign({},
              // Pull all enumerable properties, supporting properties on custom Errors
              value,
              // Explicitly pull Error's non-enumerable properties
              {
                "name": value.name,
                "message": value.message,
                "stack": value.stack,
                "cause": value.cause,
              }
            );
          }

          return value;
        };

        //if (typeof message !== 'string') {
        //  // Ensure message is cast to a string if another type of object is passed in
        //  message = JSON.stringify(message, jsonFriendlyErrorReplacer);
        //}

        if (error) {
          errorString = JSON.stringify(error, jsonFriendlyErrorReplacer);
        }
      }
      catch (errInner) { }

      let options = BaseHttpService.getHttpOptions();

      let body: string = JSON.stringify({ message: msg, url: logger, userAgent: userAgent, platform: platform, location: location, error: errorString });
      this.http?.post(`${this.errorUrl}${logAsWarning ? "LogJavascriptWarning" : "LogJavascriptError"}`, body, options).subscribe({ // Subscription required to trigger post
        error: console.error // Catch error and log to console
      });
    }
    catch (e) {
      console.error(e);
    }
  }

  handleHttpErrorAndRethrow<T>(url: string, body: string, httpOptions: { headers?: HttpHeaders }, service: BaseHttpService) {
    return this.handleHttpError<T>(url, body, httpOptions, service, true);
  }

  handleHttpError<T>(url: string, body: string, httpOptions: { headers?: HttpHeaders }, service: BaseHttpService, rethrow: boolean = false) {
    return (error: any, caught: Observable<T>): Observable<T> => {
      let logAsWarning: boolean = false;
      if (error instanceof HttpErrorResponse) {
        switch (error.status) {
          case 0: // Most likely caused by internet connection issue
          case 401: // Unauthorised
            logAsWarning = true;
            break;
        }
      }
      else if (error?.name == "TimeoutError") {
        logAsWarning = true;
      }
      else if (!body) {
        // Empty body, likely connection issue
        logAsWarning = true;
      }

      if (logAsWarning) {
        console.warn(error);
      }
      else {
        console.error(error);
      }

      let headers: string = BaseHttpService.tryGetHttpRequestHeaders(httpOptions);
      let message: string = `'${url}' failed with error '${error?.message}', body: '${body}', headers: '${headers}'`;
      this.logError(message, `Angular ${service?.friendlyName ?? this?.friendlyName} handleHttpError`, error, logAsWarning);

      if (rethrow) {
        //console.log("rethrow");
        throw error;
      }
      else {
        // Let the app keep running by returning an empty result.
        return of(null as T);
      }
    };
  }

  handleOperationError<T>(operation = 'Operation', service: BaseHttpService, rethrow: boolean = false) {
    return (error: any, caught: Observable<T>): Observable<T> => {
      let logAsWarning: boolean = false;

      if (logAsWarning) {
        console.warn(error);
      }
      else {
        console.error(error);
      }

      let message: string = `'${operation}' failed with error '${error?.message}'`;
      this.logError(message, `Angular ${service?.friendlyName ?? this?.friendlyName} handleOperationError`, error, logAsWarning);

      if (rethrow) {
        //console.log("rethrow");
        throw error;
      }
      else {
        // Let the app keep running by returning an empty result.
        return of(null as T);
      }
    };
  }

}
